import { useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { api_without_cancellation } from "../../../api/api";
import avatarImage from "../../../assets/images/avatar.svg";
import birthdayIcon from "../../../assets/images/birthdayicon.svg";
import incidentIcon from "../../../assets/images/incident.svg";
import {
  calculateAge,
  formatCalendarDate,
  formatPhoneNumber,
  formatTimestampDate,
  setCaseId,
  setClientId,
} from "../../../Utils/helper";
import { setBlurEffect } from "../../../Redux/header_blur/action";
import { setHeaderName } from "../../../Redux/header_name/action";
import { LoadingOverlay } from "../../../Providers/ProtectedRoute";
import mixColorWithWhite from "../../TreatmentPage/utils/helperFn";

import ReviewModal from "./review-modal";
import ReviewModalCoCounselling from "./review-modal-co-counselling";
import useGetInvites, { useGetCoCounsellingInvites } from "./hooks/useGetInvites";
import "./IncomingReferrals.css";

// Empty stripes match the minimum data-row height; multiline content can be taller.
const ROW_HEIGHT = 94;
const COLUMN_COUNT = 10;

const ReferralCaseCell = ({ client, caseType, incidentDate, onOpen, isOpening }) => {
  const profilePic = client?.profile_pic_19p || client?.profile_pic_29p || client?.profile_pic;
  const name = client?.first_name
    ? `${client?.last_name || ""}, ${client.first_name}`
    : client?.last_name || "";
  const age = calculateAge(client?.birthday);

  return (
    <td className="incoming-referrals-cell incoming-referrals-case">
      <div className="incoming-referrals-line">
        <span className="ic m-r-5 ic-avatar ic-15 has-avatar-icon has-cover-img">
          <img
            src={profilePic || avatarImage}
            className="output-3 theme-ring"
            style={profilePic ? undefined : { background: "var(--primary-30)" }}
            alt=""
            onError={(event) => {
              event.currentTarget.src = avatarImage;
              event.currentTarget.style.background = "var(--primary-30)";
            }}
          />
        </span>
        {onOpen ? (
          <button
            type="button"
            className="incoming-referrals-text incoming-referrals-open-case"
            aria-label={`Open case for ${name || "client"}`}
            disabled={isOpening}
            onClick={(event) => {
              event.stopPropagation();
              onOpen();
            }}
          >
            {name}
          </button>
        ) : (
          <span className="incoming-referrals-text">{name}</span>
        )}
      </div>

      <div className="incoming-referrals-line justify-content-between">
        <div className="d-flex align-items-center">
          <img src={birthdayIcon} className="incoming-referrals-icon m-r-5" alt="" />
          <span>{formatCalendarDate(client?.birthday)}</span>
        </div>
        {age !== "" && (
          <div className="d-flex align-items-center m-l-5">
            <span className="text-primary-25 m-r-5">age</span>
            <span>{age}</span>
          </div>
        )}
      </div>

      <div className="incoming-referrals-line">
        {caseType?.casetype_icon && (
          <img src={caseType.casetype_icon} className="incoming-referrals-icon m-r-5" alt="" />
        )}
        <span className="incoming-referrals-text">{caseType?.name}</span>
      </div>

      <div className="incoming-referrals-line">
        <img src={incidentIcon} className="incoming-referrals-icon m-r-5" alt="" />
        <span>{formatCalendarDate(incidentDate)}</span>
      </div>
    </td>
  );
};

const ReferralStatusCell = ({ referrer, onReview }) => {
  if (referrer?.is_accepted) {
    return (
      <td className="incoming-referrals-cell">
        <div className="incoming-referrals-line justify-content-center">Accepted</div>
      </td>
    );
  }

  if (referrer?.is_accepted === false) {
    return (
      <td className="incoming-referrals-cell">
        <div className="incoming-referrals-line justify-content-center">Rejected</div>
        {referrer?.rejection_reason && (
          <div className="incoming-referrals-line">
            <span className="incoming-referrals-text">{referrer.rejection_reason}</span>
          </div>
        )}
      </td>
    );
  }

  return (
    <td className="incoming-referrals-cell">
      <div className="incoming-referrals-line justify-content-center">
        <button
          type="button"
          onClick={() => onReview(referrer)}
          className="height-21 btn btn-primary d-flex align-items-center justify-content-center p-l-5 p-r-5"
        >
          Review
        </button>
      </div>
    </td>
  );
};

const IncomingReferrals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const openingCaseRef = useRef(false);
  const [isOpeningCase, setIsOpeningCase] = useState(false);
  const [openingCase, setOpeningCase] = useState(null);

  const openAcceptedReferral = async (referrer) => {
    if (!referrer?.is_accepted || openingCaseRef.current) return;
    const clientId = referrer.client?.id;
    const caseId = referrer.case_id;
    if (!clientId || !caseId || !["string", "number"].includes(typeof caseId)) {
      toast.error("This referral is missing its client or case. Refresh and try again.");
      return;
    }

    openingCaseRef.current = true;
    setIsOpeningCase(true);
    const caseData = {
      id: caseId,
      for_client: { ...referrer.client, id: clientId },
      case_type: referrer.case_type,
      incident_date: referrer.incident_date,
    };
    setOpeningCase(caseData);
    try {
      await api_without_cancellation.get(`/api/switch_client/${clientId}/${caseId}/1/`);
      setClientId(clientId);
      setCaseId(caseId);
      dispatch(setBlurEffect(false));
      dispatch(setHeaderName("Case"));
      navigate(`/bp-home/${clientId}/${caseId}`, {
        state: { openingCase: caseData, refreshCaseData: true },
      });
    } catch (error) {
      console.error("Unable to open the accepted referral's case:", error);
      toast.error("Unable to open this case. Please try again.");
    } finally {
      openingCaseRef.current = false;
      setIsOpeningCase(false);
      setOpeningCase(null);
    }
  };

  const handleRowClick = (event, referrer) => {
    if (event.target.closest("button, a, input, select, textarea, label")) return;
    openAcceptedReferral(referrer);
  };

  const [reviewModal, setReviewModal] = useState(false);
  const [inviteData, setInviteDate] = useState(null);
  const handleReviewModal = (referrer) => {
    setReviewModal(true);
    setInviteDate(referrer);
  };

  const [reviewModalCoCounselling, setReviewModalCoCounselling] = useState(false);
  const [inviteDataCoCounselling, setInviteDataCoCounselling] = useState(null);
  const handleAttorneyReviewModal = (referrer) => {
    setReviewModalCoCounselling(true);
    setInviteDataCoCounselling(referrer);
  };

  const { data: getInvites, fetchInvites } = useGetInvites();
  const { data: getCoCounsellingInvite, fetchCoCounsellingInvite } = useGetCoCounsellingInvites();

  const providerInvites = getInvites?.invites ?? [];
  const coCounsellingInvites = getCoCounsellingInvite?.invites ?? [];

  const tableRef = useRef(null);
  const scrollRef = useRef(null);
  // { full: whole fake rows, partial: leftover px filled by one short row }
  const [filler, setFiller] = useState({ full: 0, partial: 0 });

  // Fill only the unused space inside the bounded table viewport. Scroll
  // position never affects this measurement, and filler cannot create overflow.
  useLayoutEffect(() => {
    const table = tableRef.current;
    const viewport = scrollRef.current;
    if (!table || !viewport) return undefined;

    let frameId;
    const measure = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const t = tableRef.current;
        if (!t) return;

        let used = t.tHead?.getBoundingClientRect().height ?? 0;
        t.querySelectorAll("tbody tr[data-kind='data']").forEach((row) => {
          used += row.getBoundingClientRect().height;
        });

        const remaining = Math.max(0, Math.floor(viewport.clientHeight - used));
        const next = {
          full: Math.floor(remaining / ROW_HEIGHT),
          partial: remaining % ROW_HEIGHT,
        };
        setFiller((prev) =>
          prev.full === next.full && prev.partial === next.partial ? prev : next
        );
      });
    };

    measure();
    window.addEventListener("resize", measure);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(viewport);
    observer?.observe(table);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [providerInvites.length, coCounsellingInvites.length]);

  const renderRowNumber = (number) => (
    <td className="incoming-referrals-cell incoming-referrals-number">
      <div className="incoming-referrals-line justify-content-center">{number}</div>
    </td>
  );

  const renderSentDate = (sentAt) => (
    <td className="incoming-referrals-cell">
      <div className="incoming-referrals-line justify-content-center">
        {formatTimestampDate(sentAt)}
      </div>
    </td>
  );

  return (
    <>
      {openingCase &&
        createPortal(
          <LoadingOverlay
            caseData={openingCase}
            clientId={openingCase.for_client.id}
            caseId={openingCase.id}
            destinationPath={`/bp-home/${openingCase.for_client.id}/${openingCase.id}`}
            overPage
          />,
          document.body
        )}
      <div
        ref={scrollRef}
        className="incoming-referrals-scroll"
        role="region"
        aria-label="Incoming referrals"
        aria-busy={isOpeningCase}
        tabIndex={0}
      >
        <table
          ref={tableRef}
          className="table table-borderless table-striped table-treatment has-specialty-icon has-height-25 mt-0 mb-0"
          id="incoming-referrals-table"
        >
          <thead>
            <tr id="tb-header">
              <th className="incoming-referrals-number" />
              <th className="text-center">Date</th>
              <th className="incoming-referrals-spacer" />
              <th className="text-center">Case</th>
              <th className="incoming-referrals-spacer" />
              <th className="text-center">Source</th>
              <th className="incoming-referrals-spacer" />
              <th className="text-center">Referral Note</th>
              <th className="incoming-referrals-spacer" />
              <th className="text-center">Accepted</th>
            </tr>
          </thead>
          <tbody>
            {providerInvites.map((referrer, idx) => (
              <tr
                key={`provider-${referrer?.id ?? idx}`}
                data-kind="data"
                className={`incoming-referrals-row ${referrer?.is_accepted ? "incoming-referrals-row-openable" : ""}`}
                onClick={
                  referrer?.is_accepted ? (event) => handleRowClick(event, referrer) : undefined
                }
              >
                {renderRowNumber(idx + 1)}
                {renderSentDate(referrer?.sent_at)}
                <td className="incoming-referrals-spacer" />
                <ReferralCaseCell
                  client={referrer?.client}
                  caseType={referrer?.case_type}
                  incidentDate={referrer?.incident_date}
                  onOpen={referrer?.is_accepted ? () => openAcceptedReferral(referrer) : undefined}
                  isOpening={isOpeningCase}
                />
                <td className="incoming-referrals-spacer" />
                <td className="incoming-referrals-cell">
                  <div
                    className="incoming-referrals-line"
                    style={{ background: mixColorWithWhite(referrer?.specialty?.color, 10) }}
                  >
                    {referrer?.specialty && (
                      <>
                        <span
                          className="incoming-referrals-specialty m-r-5"
                          style={{ background: referrer.specialty.color }}
                        >
                          {referrer.specialty.name?.[0]}
                        </span>
                        <span className="incoming-referrals-text p-r-5">
                          {referrer.specialty.name}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="incoming-referrals-line">
                    <span className="incoming-referrals-text">
                      {referrer?.provider_office_name}
                    </span>
                  </div>
                  <div className="incoming-referrals-line">
                    {formatPhoneNumber(referrer?.provider_phone)}
                  </div>
                  <div className="incoming-referrals-line">
                    <span className="incoming-referrals-text">{referrer?.provider_email}</span>
                  </div>
                </td>
                <td className="incoming-referrals-spacer" />
                <td className="incoming-referrals-cell" />
                <td className="incoming-referrals-spacer" />
                <ReferralStatusCell referrer={referrer} onReview={handleReviewModal} />
              </tr>
            ))}

            {coCounsellingInvites.map((referrer, idx) => (
              <tr
                key={`co-counsel-${referrer?.id ?? idx}`}
                data-kind="data"
                className={`incoming-referrals-row ${referrer?.is_accepted ? "incoming-referrals-row-openable" : ""}`}
                onClick={
                  referrer?.is_accepted ? (event) => handleRowClick(event, referrer) : undefined
                }
              >
                {renderRowNumber(providerInvites.length + idx + 1)}
                {renderSentDate(referrer?.sent_at)}
                <td className="incoming-referrals-spacer" />
                <ReferralCaseCell
                  client={referrer?.client}
                  caseType={referrer?.case_type}
                  incidentDate={referrer?.incident_date}
                  onOpen={referrer?.is_accepted ? () => openAcceptedReferral(referrer) : undefined}
                  isOpening={isOpeningCase}
                />
                <td className="incoming-referrals-spacer" />
                <td className="incoming-referrals-cell">
                  <div className="incoming-referrals-line">
                    <span className="incoming-referrals-text">
                      {referrer?.attorney_office_name}
                    </span>
                  </div>
                  <div className="incoming-referrals-line">
                    {formatPhoneNumber(referrer?.attorney_phone)}
                  </div>
                  <div className="incoming-referrals-line">
                    <span className="incoming-referrals-text">{referrer?.attorney_email}</span>
                  </div>
                </td>
                <td className="incoming-referrals-spacer" />
                <td className="incoming-referrals-cell" />
                <td className="incoming-referrals-spacer" />
                <ReferralStatusCell referrer={referrer} onReview={handleAttorneyReviewModal} />
              </tr>
            ))}

            {Array.from({ length: filler.full }, (_, index) => (
              <tr
                key={`fake-${index}`}
                data-kind="fake"
                aria-hidden="true"
                style={{ height: `${ROW_HEIGHT}px` }}
              >
                <td colSpan={COLUMN_COUNT} />
              </tr>
            ))}

            {filler.partial > 0 && (
              <tr
                key="fake-partial"
                data-kind="fake"
                aria-hidden="true"
                style={{ height: `${filler.partial}px` }}
              >
                <td
                  colSpan={COLUMN_COUNT}
                  className="incoming-referrals-partial"
                  style={{ height: `${filler.partial}px` }}
                />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {reviewModal && (
        <ReviewModal
          show={reviewModal}
          handleClose={() => setReviewModal(false)}
          inviteData={inviteData}
          fetchInvites={fetchInvites}
        />
      )}

      {reviewModalCoCounselling && (
        <ReviewModalCoCounselling
          show={reviewModalCoCounselling}
          handleClose={() => setReviewModalCoCounselling(false)}
          inviteData={inviteDataCoCounselling}
          fetchInvites={fetchCoCounsellingInvite}
        />
      )}
    </>
  );
};

export default IncomingReferrals;
